import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import useAuthContext from 'hooks/useAuthContext';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateGeneralStudentAgreement } from 'api/hooks/private/tenant/UpdateGeneralStudentAgreement';
import { useGetGeneralStudentAgreement } from 'api/hooks/private/tenant/GetGeneralStudentAgreement';
import AgreementForm, { AgreementOutputValues } from 'components/AgreementForm';
import { SerializedEditorState } from 'lexical';
import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import PageContainer from 'components/PageContainer';
import TermsTheme from 'components/Editor/themes/TermsTheme';


export function TermsOfService() {
    const { tenantId } = useAuthContext();
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const updateGeneralStudentAgreement = useUpdateGeneralStudentAgreement();

    // fetch the agreement
    const agreement = useGetGeneralStudentAgreement({
        variables: {
            tenant_id: tenantId ?? '',
        },
        enabled: !!tenantId,
    });

    const onSubmit = async (data: AgreementOutputValues) => {
        try {
            // show confirmation dialog
            const confirmed = await dialogs.confirm(`Update General Terms of Service?`, {
                okText: 'Update',
                cancelText: 'Cancel',
            });

            // if user cancels, return
            if (!confirmed) {
                return;
            }

            const result = await updateGeneralStudentAgreement.mutateAsync({
                tenantId: tenantId ?? '',
                data: {
                    content: data.content as SerializedEditorState
                },
            });

            if (result) {
                // close the dialog
                // handleClose();

                // invalidate the query key
                queryClient.invalidateQueries({
                    queryKey: ['general_student_agreement'],
                });

                // show success message
                dialogs.open(FeedbackDialog, {
                    icon: 'success',
                    title: 'General Terms of Service Updated',
                });
            }
        } catch (error) {
            dialogs.open(FeedbackDialog, {
                title: 'Error',
                message: getApiErrorMessage(error),
            });
        }
    };


    return (
        <PageContainer title="General Terms of Service">
            <RichTextEditorProvider namespace='general-terms-of-service' theme={TermsTheme}>
                <AgreementForm
                    initialData={{
                        content: agreement.data?.agreement_versions?.[0].content
                    }}
                    isLoading={agreement.isLoading}
                    onSubmit={onSubmit}
                />
            </RichTextEditorProvider>
        </PageContainer>
    );
}

export default TermsOfService;